import type { FitThesis } from '@ispot/shared'

/**
 * Three-stage canvas mapping. The 9-block fit thesis groups into three
 * narrative phases that mirror the Market / Business / Solution ontology:
 *
 *   customer    → persona, jtbd, pain          (Market teal)
 *   competitor  → competitor, flaw, consequence (Business blue)
 *   solution    → pitch, benefitA, benefitB    (Solution gold)
 */
export type Stage = 'customer' | 'competitor' | 'solution'

export interface StageProgress {
  customer: number    // 0..3
  competitor: number  // 0..3
  solution: number    // 0..3
}

const EMPTY: StageProgress = { customer: 0, competitor: 0, solution: 0 }

function hasText(s: string | null | undefined): boolean {
  return typeof s === 'string' && s.trim().length > 0
}

export function stageProgressFor(thesis: FitThesis | null | undefined): StageProgress {
  if (!thesis) return EMPTY
  return {
    customer:
      (thesis.personaSessionId ? 1 : 0) +
      (thesis.jtbdId ? 1 : 0) +
      (thesis.painId ? 1 : 0),
    competitor:
      (thesis.competitorId ? 1 : 0) +
      (thesis.competitorFlawId ? 1 : 0) +
      (hasText(thesis.consequenceText) ? 1 : 0),
    solution:
      (hasText(thesis.solutionPitch) ? 1 : 0) +
      (thesis.benefitACapabilityId ? 1 : 0) +
      (thesis.benefitBCapabilityId ? 1 : 0),
  }
}

export function totalProgress(p: StageProgress): number {
  return p.customer + p.competitor + p.solution
}

/**
 * Stage-aware Skerpa coach line. Returns null for validated theses (silent
 * is the right voice when there's nothing left to coach).
 */
export function coachLineFor(
  thesis: FitThesis | null | undefined,
  personaName: string,
): string | null {
  if (!thesis) {
    return `Start with what ${personaName} needs to achieve, in one sentence.`
  }
  if (thesis.status === 'validated') return null

  const p = stageProgressFor(thesis)

  if (p.customer < 3) {
    if (p.customer <= 1) return `${personaName}'s job is named. Now sharpen the pull — what root problem makes them look for a solution?`
    return 'Customer side almost there. Lock the root pain before naming competitors.'
  }
  if (p.competitor < 3) {
    if (p.competitor === 0) return `Customer is clear. Who's failing ${personaName} today?`
    if (p.competitor === 1) return 'Competitor named. What specifically falls short for them?'
    return 'One step from the consequence. What goes wrong if this never gets solved?'
  }
  if (p.solution < 3) {
    if (p.solution === 0) return `${personaName} knows what's broken and who's failing. Name what your solution does.`
    if (p.solution === 1) return 'Pitch is in. How does your solution solve the overarching problem?'
    return 'One block from a complete thesis. How do you close the competitor gap?'
  }
  if (thesis.status === 'complete') return 'All nine blocks committed. Ready to validate against evidence.'
  return null
}

/**
 * Lower rank = needs more attention. Used to sort rows within a persona group
 * so the things to work on surface first.
 */
export function attentionRank(thesis: FitThesis | undefined): number {
  if (!thesis) return 2  // empty cell — surface above complete/validated
  switch (thesis.status) {
    case 'gap':       return 0
    case 'draft':     return 1
    case 'empty':     return 2
    case 'complete':  return 3
    case 'validated': return 4
    default:          return 2
  }
}
